import React, { useEffect, Component, ErrorInfo, ReactNode } from 'react'
import { RouterProvider } from 'react-router-dom'
import { App as AntdApp, ConfigProvider, Button, Result } from 'antd'
import type { Locale } from 'antd/es/locale'
import zhCN from 'antd/locale/zh_CN'
import zhTW from 'antd/locale/zh_TW'
import enUS from 'antd/locale/en_US'
import { I18nextProvider, useTranslation } from 'react-i18next'
import '@/locales'
import i18n from '@/locales'
import useI18nStore from '@/store/i18nStore'
import themeConfig from '@/theme/themeConfig'
import { router } from '@/router'
import { resolveBrandName, buildDocumentTitle } from '@/config/brand'
import { setMessageInstance } from '@/utils/antdStatic'

const antdLocaleMap: Record<string, Locale> = {
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'en-US': enUS,
}

// ── Error fallback UI (function component so hooks work) ─────────────────────
function ErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Result
        status="error"
        title={t('error.boundary.title')}
        subTitle={error?.message || t('error.boundary.subTitle')}
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            {t('error.boundary.reload')}
          </Button>
        }
      />
    </div>
  )
}

// ── Error boundary (class component required by React) ───────────────────────
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />
    }
    return this.props.children
  }
}

/**
 * 把 antd App 的 message 实例交给非组件代码（请求拦截器等）使用。
 * 必须渲染在 <AntdApp> 内部才拿得到 context 实例。
 */
function AntdStaticBridge({ children }: { children: ReactNode }) {
  const { message } = AntdApp.useApp()

  useEffect(() => {
    setMessageInstance(message)
  }, [message])

  return <>{children}</>
}

function App() {
  const locale = useI18nStore((state) => state.locale)

  useEffect(() => {
    i18n.changeLanguage(locale)
  }, [locale])

  // 浏览器标签页标题：品牌名（env 优先，回退 i18n）+ 本地化后缀，随语言切换更新
  useEffect(() => {
    const name = resolveBrandName(i18n.t('common.appName'))
    document.title = buildDocumentTitle(name, i18n.t('common.appTitleSuffix'))
  }, [locale])

  return (
    <I18nextProvider i18n={i18n}>
      <ConfigProvider theme={themeConfig} locale={antdLocaleMap[locale] ?? zhCN}>
        {/*
          保留 App 默认渲染的 div：antd 6 默认开启 cssVar，component={false} 会让
          CSS 变量没有承载节点并告警。这个 div 不影响布局 —— AppLayout 根元素用的是
          h-screen（视口高度），不依赖父级高度。
        */}
        <AntdApp>
          <AntdStaticBridge>
            <ErrorBoundary>
              <RouterProvider router={router} />
            </ErrorBoundary>
          </AntdStaticBridge>
        </AntdApp>
      </ConfigProvider>
    </I18nextProvider>
  )
}

export default App
